import dayjs from '@/dayjs'
import {PatientSmall} from '@/features/patients/types'
import {useGetProjectsByPatientDate} from '@/features/projects/queries/useGetProjectsByPatientDate'
import {ProjectSmall} from '@/features/projects/types'
import {useToggle} from '@/hooks/useToggle'
import {useCallback, useEffect, useMemo} from 'react'

/**
 * Custom hook to fetch and manage the project associated with a patient and a given report date.
 * This hook:
 * - Retrieves the project linked to the specified `patientId` and `date` using `useGetProjectByPatientDate`.
 * - Automatically updates the current project state (`setCurrentProject`) when the fetched data changes.
 * - Handles error state visibility using `useToggle`, showing an error element if the query fails.
 */

interface UseProjectsByPatientDate {
    patientId: PatientSmall['id']
    date: Date
    currentProject?: ProjectSmall | null
    setCurrentProject?: (project: ProjectSmall | null) => void
    enabled?: boolean
}

export const useProjectsByPatientDate = ({
    patientId,
    date,
    currentProject,
    setCurrentProject,
    enabled = true
}: UseProjectsByPatientDate) => {
    // Manage the visibility of the error element using a toggle hook
    const {isActive: isErrorElement, on: showErrorElement, off: hideErrorElement} = useToggle()

    // Fetch the project associated with the given patient and report date
    const {
        data: projectsByPatient,
        status: projectsByPatientStatus,
        isError,
        isLoading
    } = useGetProjectsByPatientDate({
        patientId,
        date: dayjs(date).format('YYYY-MM-DD'),
        // disable cache and refetch on mount to avoid issues with the project selection
        options: {
            gcTime: 0,
            staleTime: 0,
            refetchOnMount: true,
            refetchOnWindowFocus: false,
            enabled
        }
    })

    // memoize the project to avoid rerendering triggered by useEffect
    const projects = useMemo(() => projectsByPatient || null, [projectsByPatient])

    // Update the project state when the fetched project changes
    const handleSetCurrentProject = useCallback(
        (project: ProjectSmall) => {
            if (projects && projects.length > 0 && projects[0]?.id != currentProject?.id && currentProject !== null) {
                setCurrentProject?.(project)
            }
        },
        [currentProject, projects, setCurrentProject]
    )

    useEffect(() => {
        projects && handleSetCurrentProject(projects[0])
    }, [projects])

    // Manage the visibility of the error element based on the query status
    useEffect(() => {
        if (projectsByPatientStatus == 'error') {
            showErrorElement()
        } else if (projectsByPatientStatus == 'success') {
            hideErrorElement()
        }
    }, [projectsByPatientStatus, showErrorElement, hideErrorElement])

    return {isErrorElement, hideErrorElement, projects, isError, isLoading}
}
